import { useMemo, useState } from 'react'
import { errorHandler } from '../../../../../services/util/error.service'
import * as MotoristaHttp from '../../../../../services/api/https/registration/motorista.http'
import { DateTime } from 'luxon'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  SAVE_DRIVER: 'Salvando informações do motorista',
  SUCCESS: 'Salvo com sucesso',
}

const PHONE_ERROR_MESSAGE = 'O número de telefone deve ter 11 dígitos (com DDD)'

const INITIAL_DRIVER = {
  id: undefined,
  name: '',
  tp_id: 0,
  cnh_number: '',
  cnh_type: '',
  cnh_expiration: '__/__/____',
  toxicologico_expiration: '__/__/____',
  phone: '(__)_____-____',
  address: '',
  photo_url: '',
  actived: false,
  cpf: '',
  password: '',
}

const INITIAL_STATE = {
  motorista: { ...INITIAL_DRIVER },
  savedMotorista: {},
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
}

export default function useMotoristaModal() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    // Helpers
    function extractPhoneDigits(phoneValue) {
      return phoneValue ? String(phoneValue).replace(/\D/g, '') : ''
    }

    function isPhoneInvalid(phoneValue) {
      const phoneDigits = extractPhoneDigits(phoneValue)
      return phoneDigits.length > 0 && phoneDigits.length < 11
    }

    function formatDriverToPost({ created_at, updated_at, ...motoristaWithoutDBTimeStamp }) {
      const { cnh_expiration, phone, toxicologico_expiration } = motoristaWithoutDBTimeStamp
      const phoneDigits = extractPhoneDigits(phone)

      const formattedMotorista = {
        ...motoristaWithoutDBTimeStamp,
        cnh_expiration:
          cnh_expiration !== '' && cnh_expiration !== '__/__/____' && cnh_expiration
            ? DateTime.fromFormat(cnh_expiration, 'dd/MM/yyyy').toJSDate()
            : '',
        toxicologico_expiration:
          toxicologico_expiration !== '' &&
          toxicologico_expiration !== '__/__/____' &&
          toxicologico_expiration
            ? DateTime.fromFormat(toxicologico_expiration, 'dd/MM/yyyy').toJSDate()
            : '',
        phone: phoneDigits ? phoneDigits : null,
      }
      return formattedMotorista
    }

    return {
      // Actions
      saveMotorista(motorista, selectedTp) {
        if (isPhoneInvalid(motorista.phone)) {
          setState(prevState => ({
            ...prevState,
            errorMessage: PHONE_ERROR_MESSAGE,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
          return
        }

        const payload = formatDriverToPost(motorista)
        if (!payload.password) delete payload.password
        MotoristaHttp.saveMotorista({ ...payload, tp_id: selectedTp })
          .then(motoristaRes =>
            setState(prevState => ({
              ...prevState,
              savedMotorista: motoristaRes,
              loadingMessage: LOAD_MESSAGE.SUCCESS,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },

      resetState() {
        setState(prevState => ({
          ...prevState,
          motorista: { ...INITIAL_DRIVER },
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      resetErrorMessage() {
        setState(prevState => ({ ...prevState, errorMessage: null }))
      },

      validateValue(value) {
        return value ? value : ''
      },

      // Events
      onInit(motorista) {
        if (motorista)
          setState(prevState => {
            prevState.motorista = { ...INITIAL_DRIVER, ...motorista }
            prevState.motorista.password = ''
            const phoneValue = motorista.phone ? String(motorista.phone) : ''
            prevState.motorista.cnh_expiration = DateTime.fromISO(motorista.cnh_expiration)
              .plus({ hours: 3 })
              .toFormat('dd/MM/yyyy')
            prevState.motorista.toxicologico_expiration = DateTime.fromISO(
              motorista.toxicologico_expiration,
            )
              .plus({ hours: 3 })
              .toFormat('dd/MM/yyyy')
            prevState.motorista.phone = phoneValue.trim() === '' ? INITIAL_DRIVER.phone : phoneValue
            return { ...prevState }
          })
        else {
          setState(prevState => {
            prevState.motorista = { ...INITIAL_DRIVER }
            return { ...prevState }
          })
        }
      },

      onInputChange(name, value) {
        setState(prevState => {
          prevState.motorista[name] = value
          return { ...prevState }
        })
      },

      onBtnSalvarClicked(selectedTp) {
        if (!selectedTp || selectedTp === 0)
          setState(prevState => ({ ...prevState, errorMessage: 'Selecione o Operador Logístico' }))
        else
          setState(prevState => {
            if (isPhoneInvalid(prevState.motorista.phone))
              return {
                ...prevState,
                errorMessage: PHONE_ERROR_MESSAGE,
                loadingMessage: LOAD_MESSAGE.NONE,
              }

            return { ...prevState, loadingMessage: LOAD_MESSAGE.SAVE_DRIVER }
          })
      },
    }
  }, [])

  return { state, localDispatch }
}
